import {
    MarketTemplatesType,
    PopupType,
} from 'src/components/SportPopup/SportMarketTemplates/SportMarketTemplates.state';
import { OpenapiProxyStaffSportResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_sport';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import React, { useState } from 'react';
import { Popup } from 'src/components/Popup/Popup';
import MarketTemplateCreationForm from 'src/components/MarketTemplateCreationForm';
import SelectionTemplateCreationForm from 'src/components/SelectionTemplateCreationForm';
import { buildNewSelection } from 'src/components/SportPopup/SportMarketTemplates/MarketTemplates.utills';

type PopupPropsType = {
    onClose: (type: PopupType) => void;
    sportId: string;
    templates: MarketTemplatesType | 'loading';
    marketTemplateId?: string;
};

type EventTemplatesType = OpenapiProxyStaffSportResponse200Type['eventTemplates'];
class PopupState {
    public readonly sportResource: Resource<OpenapiProxyStaffSportResponse200Type>;
    public constructor(
        public readonly sdkStaff: SdkStaff,
        public readonly sportId: string,
        public readonly props: PopupPropsType
    ) {
        this.sportResource = new Resource(async (): Promise<OpenapiProxyStaffSportResponse200Type> => {
            return await this.sdkStaff.getSport(sportId);
        });
    }

    public get eventTemplates(): EventTemplatesType | null {
        const sportRes = this.sportResource.get();
        if (sportRes.type === 'ready') {
            return sportRes.value.eventTemplates;
        }

        return null;
    }

    public onSaveMarketTemplate = async (data: {
        marketSubtype: string;
        eventTemplateId: string;
        name: string;
        id: string;
        displayOrder?: number;
    }): Promise<void> => {
        const { sportId, templates } = this.props;
        if (templates === 'loading') {
            return;
        }
        const dateTimestamp = Date.now();

        this.props.onClose('isMarketTemplateOpen');

        // generate a market id
        data.id = `${dateTimestamp.toString(36)}-${dateTimestamp.toString(34)}`;
        data.displayOrder =
            templates.length > 0
                ? // eslint-disable-next-line prefer-spread
                  Math.max.apply(
                      Math,
                      templates.map((t) => t.displayOrder)
                  ) + 1
                : 0;

        await this.sdkStaff.createMarketTemplate({
            requestBody: {
                displayOrder: data.displayOrder,
                eventTemplateId: data.eventTemplateId,
                marketSubtype: data.marketSubtype,
                name: data.name,
                sportId,
                templateId: data.id,
            },
        });
    };

    public saveSelectionTemplate = async (data: { name: string }): Promise<void> => {
        const template =
            this.props.templates === 'loading'
                ? undefined
                : this.props.templates.find((template) => template.id === this.props.marketTemplateId);
        const selectionsNewTradingRaw = template?.selectionsNewTrading ?? undefined;

        const newSelectionForNewTrading =
            selectionsNewTradingRaw === undefined ? undefined : buildNewSelection(template, data.name);

        this.props.onClose('isSelectionTemplateOpen');
        await this.sdkStaff.postMarketTemplateSelectionCreate({
            requestBody: {
                sportId: this.props.sportId,
                eventTemplateId: template?.eventTemplate?.id ?? 'empty',
                marketTemplateId: this.props.marketTemplateId ?? 'empty',
                data: {
                    name: newSelectionForNewTrading === undefined ? data.name : undefined,
                    displayOrder: newSelectionForNewTrading === undefined ? 0 : undefined,
                    tags: {},
                    matchingMarkets: template?.matchingMarkets,
                    selectionsNewTrading: newSelectionForNewTrading,
                },
            },
        });
    };
}

export const MarketPopup = observer('renderMarketPopup', (props: PopupPropsType) => {
    const { sdkStaff } = useAppStateContext();
    const [state] = useState(() => new PopupState(sdkStaff, props.sportId, props));

    return (
        <Popup
            title='Add Market Template'
            className='add-market-template-popup'
            priority={2000}
            active={true}
            onClose={() => props.onClose('isMarketTemplateOpen')}
        >
            <MarketTemplateCreationForm
                eventTemplates={
                    state.eventTemplates === null
                        ? []
                        : state.eventTemplates.map((template) => ({ id: template.id, label: template.name }))
                }
                onSubmit={state.onSaveMarketTemplate}
            />
        </Popup>
    );
});

export const SelectionPopup = observer('SelectionPopup', (props: PopupPropsType) => {
    const { sdkStaff } = useAppStateContext();
    const [state] = useState(() => new PopupState(sdkStaff, props.sportId, props));

    return (
        <Popup
            title='Add Selection Template'
            className='add-selection-template-popup'
            priority={2000}
            active={true}
            onClose={() => props.onClose('isSelectionTemplateOpen')}
        >
            <SelectionTemplateCreationForm onSubmit={state.saveSelectionTemplate} />
        </Popup>
    );
});
